import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formToPayload, resourceToForm, ORG_TYPES } from "@/lib/orgApi";
import { ORG } from "@/constants/testIds";

const BLANK = {
  name: "", active: true, type: "prov",
  npi: "", taxId: "", clia: "",
  phone: "", fax: "", email: "",
  line1: "", line2: "", city: "", state: "", postalCode: "", country: "US",
  alias: "", contactName: "", contactPhone: "",
};

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-teal-700">{title}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, id, value, onChange, testId, placeholder, hint, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      <Input id={id} data-testid={testId} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
      {hint && <p className="text-helper text-slate-400">{hint}</p>}
    </div>
  );
}

export default function OrganizationForm({ open, onClose, editing, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setF(editing ? resourceToForm(editing) : BLANK);
  }, [open, editing]);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required (FHIR 1..1).");
    if (!f.npi.trim() && !f.taxId.trim() && !f.clia.trim())
      return toast.error("At least one identifier (NPI, Tax ID or CLIA) is required (FHIR 1..*).");
    const payload = formToPayload(f);
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/organizations/${editing.id}`, payload);
        toast.success("Organization updated.");
      } else {
        await api.post("/organizations", payload);
        toast.success("Organization created.");
      }
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save organization.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={ORG.form}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Organization" : "New Organization"}</DialogTitle>
          <DialogDescription>FHIR R4 Organization · US Core profile</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Identity">
            <div className="sm:col-span-2">
              <Field label="Name" id="org-name" required value={f.name}
                onChange={set("name")} testId={ORG.fName} placeholder="New York Pain Specialist PLLC" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Type</Label>
              <Select value={f.type} onValueChange={set("type")}>
                <SelectTrigger data-testid={ORG.fType}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch id="org-active" checked={f.active} onCheckedChange={set("active")}
                data-testid={ORG.fActive} />
              <Label htmlFor="org-active" className="text-sm text-slate-700">Active</Label>
            </div>
            <Field label="Alias (comma separated)" id="org-alias" value={f.alias}
              onChange={set("alias")} testId={ORG.fAlias} placeholder="NY Pain, NYPS" />
          </Section>

          <Section title="Identifiers">
            <Field label="NPI" id="org-npi" value={f.npi} onChange={set("npi")}
              testId={ORG.fNpi} placeholder="1073511479" hint="system: us-npi" />
            <Field label="Tax ID / EIN" id="org-tax" value={f.taxId} onChange={set("taxId")}
              testId={ORG.fTax} placeholder="84-3921476" />
            <Field label="CLIA" id="org-clia" value={f.clia} onChange={set("clia")}
              testId={ORG.fClia} placeholder="33D1234567" />
          </Section>

          <Section title="Telecom">
            <Field label="Phone" id="org-phone" value={f.phone} onChange={set("phone")}
              testId={ORG.fPhone} placeholder="+1-212-555-0143" />
            <Field label="Fax" id="org-fax" value={f.fax} onChange={set("fax")}
              testId={ORG.fFax} placeholder="+1-212-555-0144" />
            <Field label="Email" id="org-email" value={f.email} onChange={set("email")}
              testId={ORG.fEmail} placeholder="office@example.com" />
          </Section>

          <Section title="Address">
            <div className="sm:col-span-2">
              <Field label="Address line 1" id="org-line1" value={f.line1}
                onChange={set("line1")} testId={ORG.fLine1} placeholder="245 Fifth Avenue" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Address line 2" id="org-line2" value={f.line2}
                onChange={set("line2")} testId={ORG.fLine2} placeholder="Suite 1200" />
            </div>
            <Field label="City" id="org-city" value={f.city} onChange={set("city")}
              testId={ORG.fCity} placeholder="New York" />
            <Field label="State" id="org-state" value={f.state} onChange={set("state")}
              testId={ORG.fState} placeholder="NY" />
            <Field label="Postal code" id="org-postal" value={f.postalCode}
              onChange={set("postalCode")} testId={ORG.fPostal} placeholder="10016" />
            <Field label="Country" id="org-country" value={f.country}
              onChange={set("country")} testId={ORG.fCountry} placeholder="US" />
          </Section>

          <Section title="Administrative contact">
            <Field label="Contact name" id="org-cname" value={f.contactName}
              onChange={set("contactName")} testId={ORG.fContactName} placeholder="Practice Administrator" />
            <Field label="Contact phone" id="org-cphone" value={f.contactPhone}
              onChange={set("contactPhone")} testId={ORG.fContactPhone} placeholder="+1-212-555-0145" />
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={ORG.cancelBtn}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={ORG.saveBtn}
            className="bg-teal-700 hover:bg-teal-800">
            {saving ? "Saving…" : editing ? "Save changes" : "Create organization"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
